import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmployeePeer } from '@scio3/shared-types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@scio3/ui';
import { formatDateMedium } from '@/lib/format-date';

interface Props {
  peers: EmployeePeer[];
}

/**
 * Groups peers by subnet and renders a table per subnet. Per spec §1.8,
 * grouping is client-side — backend returns a flat list.
 */
export function PeersTableBySubnet({ peers }: Props) {
  const { t } = useTranslation();

  const groups = useMemo(() => {
    const map = new Map<string, EmployeePeer[]>();
    for (const peer of peers) {
      const arr = map.get(peer.subnet) ?? [];
      arr.push(peer);
      map.set(peer.subnet, arr);
    }
    // Sort subnets alphabetically for stable display order.
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [peers]);

  if (!groups.length) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {t('No employee peers.')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="peers-grouped-by-subnet">
      {groups.map(([subnet, rows]) => (
        <section
          key={subnet}
          className="rounded-lg border bg-card"
          data-testid={`subnet-group-${subnet}`}
        >
          <header className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="text-sm font-semibold text-foreground">{subnet}</h2>
            <span className="text-xs text-muted-foreground">
              {rows.length} {t('peers')}
            </span>
          </header>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('Name')}</TableHead>
                <TableHead>{t('Owner email')}</TableHead>
                <TableHead>{t('VPN IP')}</TableHead>
                <TableHead>{t('Region')}</TableHead>
                <TableHead>{t('Created by role')}</TableHead>
                <TableHead>{t('Created')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((peer) => (
                <TableRow key={peer._id} className="hover:bg-accent/50 transition-colors">
                  <TableCell className="font-medium">{peer.name}</TableCell>
                  <TableCell className="text-muted-foreground">{peer.ownerEmail ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{peer.vpnIp}</TableCell>
                  <TableCell>{peer.region}</TableCell>
                  <TableCell className="text-muted-foreground">{peer.createdByRole}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateMedium(peer.createdAt, '—')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ))}
    </div>
  );
}
